import { toNumber } from 'lodash'
import moment from 'moment'
import React, { PropsWithChildren } from 'react'
import { toConsumableNumber } from '../../../Helper/EnhancedData/EnhancedDataUtils'
import { camelToKebab } from '../../../Helper/util'
import { EmptyTableView } from '../EmptyTableView'
import { ConsumerValueMapper } from '../Types'
import { ColumnDataTypeConfiguration, PropsWithKey, TableDataProps, TableProps, TableRowProps } from './Types'

export type ColumnFormatter = (value: any) => string
export type ColumnComparator = (elementA: any, elementB: any) => number

export const moneyFormatter = (amount: number | string | null) => {
  const amountAsNumber = Number(amount)
  if (isNaN(amountAsNumber)) {
    return `0.00`
  }
  return `${amountAsNumber.toFixed(2)}`
}
const dateFormatter = (dateTime: string) => moment(dateTime).format('M/DD/YYYY')
const numberCompare = (a: number, b: number) => (a - b)
const dateStringCompare = (a: string, b: string) => moment(a).isBefore(b) ? -1 : 1
const stringCompare = (a: string, b: string) => (a < b) ? -1 : 1
export const numberCompareWithNullLast = (a: number | null, b: number | null): number => {
  const aIsNumber = typeof a === 'number'
  const bIsNumber = typeof b === 'number'

  if (aIsNumber && bIsNumber) {
    return numberCompare(a, b)
  }

  // If a is a number, (0 - 1) = -1 (a comes first)
  // If b is a number, (1 - 0) = 1 (b comes first)
  // If both are null, (0 - 0) = 0
  return Number(bIsNumber) - Number(aIsNumber)
}

// kept this function separate from money formatter incase something changes in that function
const numberTwoDecimalsFormatter: ColumnFormatter = (value: any): string => {
  const valueAsNum: number = toNumber(value)
  return !isNaN(valueAsNum) ? `${toNumber(value).toFixed(2)}` : '0.00'
}

// shadowing a clone function for typesafety
const consumableNumberMapper: ConsumerValueMapper = (value: any): number => toConsumableNumber(value)

export const tableColumnTypes: Record<string, ColumnDataTypeConfiguration> = {
  string: {
    comparator: stringCompare,
  },
  number: {
    comparator: numberCompare,
  },
  money: {
    formatter: moneyFormatter,
    comparator: numberCompare,
    consumerValueMapper: consumableNumberMapper,
  },
  dateString: {
    formatter: dateFormatter,
    comparator: dateStringCompare,
  },
  numberCompareWithNullLast: {
    comparator: numberCompareWithNullLast,
  },
  numberTwoDecimals: {
    formatter: numberTwoDecimalsFormatter,
    comparator: numberCompare,
    consumerValueMapper: consumableNumberMapper,
  },
}

const formatCommonValueTypes = (
  node: React.ReactNode,
  formatter?: ColumnFormatter,
): React.ReactNode => {
  if (!formatter) {
    return node
  }
  // Nothing to format
  if (node == null) return node
  // Text types
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    return formatter(node)
  }
  return node
}

function TableData<T extends object> (props: PropsWithKey<TableDataProps<T>>): React.JSX.Element {
  const { rowData, column } = props
  const fieldName: keyof T = column.id
  const fieldValue: any = rowData[fieldName]
  const displayValue = formatCommonValueTypes(fieldValue, column.dataType.formatter)
  return (<td key={props.key} className={`${camelToKebab(String(column.id))}-column-data`}> {displayValue} </td>)
}

function TableRow <T extends object> (props: PropsWithKey<TableRowProps<T>>) {
  const {
    rowNumber,
    className,
    columns,
    rowData,
    actionComponent,
    rowErrorContent,
    highlightRowOnError = false,
    ...rest
  } = props

  const colorClass = (rowNumber % 2 === 0) ? 'grey-row' : 'white-row'
  const hasError: boolean = !!rowErrorContent
  const highlightRowClass = (hasError && highlightRowOnError) ? 'error-row' : ''

  return (
    <>
      <tr className={`${colorClass} ${className || ''} ${highlightRowClass}`}>
        {
          columns.map(
            column => (<TableData {...rest} rowData={rowData} column={column} key={String(column.id)}/>),
          )
        }
        { actionComponent && (
          <td className="action-cell">
            {actionComponent}
          </td>
        )}
      </tr>
      {hasError && (
        <tr className={colorClass}>
          <td colSpan={columns.length + 1} style={{ padding: '8px 16px' }}>
            {rowErrorContent}
          </td>
        </tr>
      )}
    </>
  )
}

export interface TableBodyProps<T extends object> extends TableProps<T> {
  isScrollable: boolean
}

export function TableBody<T extends object> (props: PropsWithChildren<TableBodyProps<T>>): React.JSX.Element {
  const {
    rows,
    columns,
    displayEmptyTableView = false,
    actionComponentMap,
    editingRowKey,
    renderInlineForm,
    onCancelInlineForm,
    rowErrorContentMap,
    isScrollable,
  } = props

  const dynamicRows = (
    <>
      {
        rows.map((row, index) => {
          const isRowEditing = editingRowKey !== undefined && editingRowKey !== null && String(row.key) === String(editingRowKey)

          if (isRowEditing && renderInlineForm) {
            const colorClass = (index % 2 === 0) ? 'grey-row' : 'white-row'
            return (
              <tr key={row.key} className={`${colorClass} editing-row`}>
                <td colSpan={columns.length + 1} style={{ padding: '16px' }}>
                  {renderInlineForm(row.key, onCancelInlineForm || (() => {}))}
                </td>
              </tr>
            )
          }

          return (
            <TableRow
              key={row.key}
              rowId={`${row.key}`}
              rowNumber={index}
              rowData={row}
              columns={columns}
              className={`uid-row-${index}`}
              actionComponent={actionComponentMap?.[row.key]}
              rowErrorContent={rowErrorContentMap?.[row.key]}
              highlightRowOnError={props.highlightRowsOnError}
            />
          )
        })
      }
      {displayEmptyTableView && (
        <tr>
          <td colSpan={columns.length} className="empty-table-cell">
            {props.emptyTableView || <EmptyTableView/>}
          </td>
        </tr>
      )}
    </>
  )

  return (
    <tbody className={isScrollable ? 'dyn-table-body scrollable' : 'dyn-table-body'}>
    {dynamicRows}
    </tbody>
  )
}
